import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useForm, Controller } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { type MaintenanceSettings, type TeamMember } from "@shared/schema";
import { ArrowLeft, Settings } from "lucide-react";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";

type SettingsForm = {
  defaultAssigneeId: string;
  slaUrgent: number;
  slaHigh: number;
  slaMedium: number;
  slaLow: number;
  autoBatchEnabled: boolean;
  autoBatchIdleDays: number;
  batchHoldWindowDays: number;
};

export default function AppMaintenanceSettings() {
  const queryClient = useQueryClient();
  usePageTitle("Task Settings");
  const { toast } = useToast();

  const settingsQuery = useQuery<MaintenanceSettings & { slaConfig: { urgent: number; high: number; medium: number; low: number } | null }>({
    queryKey: ["/api/app/tasks/settings"],
  });
  const teamQuery = useQuery<TeamMember[]>({ queryKey: ["/api/app/team-members"] });

  const settings = settingsQuery.data;
  const teamMembers = (teamQuery.data || []).filter((m) => m.status === "accepted");

  const { register, handleSubmit, control, reset } = useForm<SettingsForm>({
    defaultValues: {
      defaultAssigneeId: "none",
      slaUrgent: 4,
      slaHigh: 24,
      slaMedium: 72,
      slaLow: 168,
      autoBatchEnabled: false,
      autoBatchIdleDays: 14,
      batchHoldWindowDays: 30,
    },
    values: settings ? {
      defaultAssigneeId: settings.defaultAssigneeId != null ? String(settings.defaultAssigneeId) : "none",
      slaUrgent: settings.slaConfig?.urgent ?? 4,
      slaHigh: settings.slaConfig?.high ?? 24,
      slaMedium: settings.slaConfig?.medium ?? 72,
      slaLow: settings.slaConfig?.low ?? 168,
      autoBatchEnabled: settings.autoBatchEnabled ?? false,
      autoBatchIdleDays: settings.autoBatchIdleDays ?? 14,
      batchHoldWindowDays: settings.batchHoldWindowDays ?? 30,
    } : undefined,
  });

  const saveMutation = useMutation({
    mutationFn: async (data: SettingsForm) => {
      return apiRequest("POST", "/api/app/tasks/settings", {
        defaultAssigneeId: data.defaultAssigneeId === "none" ? null : parseInt(data.defaultAssigneeId, 10),
        slaConfig: {
          urgent: Number(data.slaUrgent),
          high: Number(data.slaHigh),
          medium: Number(data.slaMedium),
          low: Number(data.slaLow),
        },
        autoBatchEnabled: data.autoBatchEnabled,
        autoBatchIdleDays: Number(data.autoBatchIdleDays),
        batchHoldWindowDays: Number(data.batchHoldWindowDays),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks/settings"] });
      toast({ title: "Settings saved successfully" });
    },
    onError: () => {
      toast({ title: "Failed to save settings", variant: "destructive" });
    },
  });

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto p-6">
        <ViewOnlyBanner area="maintenance" />
        <div className="flex items-center gap-3 mb-6">
          <Link href="/app/tasks">
            <Button variant="ghost" size="sm" data-testid="link-back-maintenance">
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Back to Maintenance
            </Button>
          </Link>
        </div>

        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Settings className="h-5 w-5 text-brand" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" data-tour="maintenance-settings-heading">Maintenance Settings</h1>
              <p className="text-sm text-gray-500">Configure defaults for maintenance tasks</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="maintenance-settings" />
            <GuideButton pageKey="maintenance-settings" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="maintenance-settings" />

        <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="font-semibold text-gray-900 mb-1">Default Assignee</h2>
            <p className="text-sm text-gray-500 mb-4">Automatically assign new tasks to this team member</p>
            <Controller
              name="defaultAssigneeId"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="max-w-xs" data-testid="select-default-assignee" data-tour="maintenance-settings-rules">
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (unassigned)</SelectItem>
                    {teamMembers.map((m) => (
                      <SelectItem key={m.id} value={String(m.id)}>{m.name || m.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5" data-tour="maintenance-settings-notifications">
            <h2 className="font-semibold text-gray-900 mb-1">SLA Response Times</h2>
            <p className="text-sm text-gray-500 mb-4">Set the expected response time (in hours) for each priority level</p>
            <div className="space-y-3">
              {[
                { key: "slaUrgent", label: "Urgent", color: "bg-red-500", reg: "slaUrgent" as const },
                { key: "slaHigh", label: "High", color: "bg-orange-500", reg: "slaHigh" as const },
                { key: "slaMedium", label: "Medium", color: "bg-yellow-500", reg: "slaMedium" as const },
                { key: "slaLow", label: "Low", color: "bg-green-500", reg: "slaLow" as const },
              ].map(({ key, label, color, reg }) => (
                <div key={key} className="flex items-center gap-4">
                  <div className="flex items-center gap-2 w-24">
                    <div className={`h-2.5 w-2.5 rounded-full ${color}`} />
                    <span className="text-sm font-medium text-gray-700">{label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={1}
                      className="w-24 h-8"
                      {...register(reg, { valueAsNumber: true })}
                      data-testid={`input-sla-${key.toLowerCase()}`}
                    />
                    <span className="text-sm text-gray-500">hours</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="font-semibold text-gray-900 mb-1">Task Batching</h2>
            <p className="text-sm text-gray-500 mb-4">
              Hold small, low-priority jobs and dispatch them together as one work order. Set a task's priority to <span className="font-medium">Batch</span> to add it manually, or let idle low-priority tasks move into the batch pool automatically.
            </p>
            <label className="flex items-center gap-3 mb-4 cursor-pointer">
              <Controller
                name="autoBatchEnabled"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
                    data-testid="checkbox-auto-batch-enabled"
                  />
                )}
              />
              <span className="text-sm font-medium text-gray-700">Automatically batch idle low-priority tasks</span>
            </label>
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="w-56">
                  <span className="text-sm font-medium text-gray-700">Auto-batch after idle for</span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    className="w-24 h-8"
                    {...register("autoBatchIdleDays", { valueAsNumber: true })}
                    data-testid="input-auto-batch-idle-days"
                  />
                  <span className="text-sm text-gray-500">days</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-56">
                  <span className="text-sm font-medium text-gray-700">Resurface batched tasks after</span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    className="w-24 h-8"
                    {...register("batchHoldWindowDays", { valueAsNumber: true })}
                    data-testid="input-batch-hold-window-days"
                  />
                  <span className="text-sm text-gray-500">days</span>
                </div>
              </div>
              <p className="text-xs text-gray-400">
                Batched tasks that aren't dispatched within the hold window are returned to Low priority and resurfaced so they don't get forgotten.
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={saveMutation.isPending} data-testid="button-save-settings">
              {saveMutation.isPending ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
